import { autoUpdate, useFloating } from "@floating-ui/react-dom";
import { EntityModel, UserRole } from "@jugl-web/rest-api";
import { InteractiveContainer, Menu } from "@jugl-web/ui-kit";
import { useTranslations, cx } from "@jugl-web/utils";
import { useIsLargeOrBelow } from "@jugl-web/utils/hooks/useMediaQuery";
import { useSelectEntity } from "@web-src/modules/entities/hooks/useSelectEntity";
import { FC, useRef, useState } from "react";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { NotRemovableReasonHint } from "@web-src/modules/entities/components/NotRemovableReasonHint";
import { useEntityRemovability } from "@web-src/modules/entities/hooks/useEntityRemovability";
import { ListItemCard } from "../../pages/EntitySelectPage/components/ListItemCard";
import { ReactComponent as BellIcon } from "./assets/bell.svg";
import { ReactComponent as LeaveIcon } from "./assets/leave.svg";
import { ReactComponent as MenuIcon } from "./assets/menu.svg";
import { ReactComponent as StarsIcon } from "./assets/stars.svg";
import { ReactComponent as SelectedIcon } from "./assets/selected.svg";
import { ReactComponent as AdminIcon } from "./assets/admin-indicator.svg";
import { ReactComponent as TrashIcon } from "./assets/trash.svg";
import { EntityUpdatesTooltip } from "./components/EntityUpdatesTooltip";

export interface EntityCardProps {
  entity: EntityModel;
  variant?: "default" | "popover";
  isSelected?: boolean;
  showToastOnSwitch?: boolean;
  onLeaveEntity: () => void;
  onDeleteEntity?: () => void;
  onEntitySwitch?: () => void;
}

const TOOLTIP_APPEARANCE_DEBOUNCE_TIME_MS = 300;

export const EntityCard: FC<EntityCardProps> = ({
  entity,
  variant = "default",
  isSelected,
  showToastOnSwitch,
  onLeaveEntity,
  onDeleteEntity,
  onEntitySwitch,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const { selectEntity } = useSelectEntity();
  const { generateUrl } = useNavigation();
  const tooltipTimeoutRef = useRef<number>(undefined);
  const isSmallViewport = useIsLargeOrBelow();

  const { t } = useTranslations();

  const { notRemovable, notRemovableReason } = useEntityRemovability(
    entity.type === "cx" ? entity.id : undefined
  );

  const { refs, floatingStyles } = useFloating({
    placement: "right",
    transform: false,
    strategy: "fixed",
    whileElementsMounted: isSmallViewport ? undefined : autoUpdate,
  });

  const handleMouseEnter = () => {
    if (isTooltipVisible) {
      window.clearTimeout(tooltipTimeoutRef.current);
    } else {
      tooltipTimeoutRef.current = window.setTimeout(() => {
        setIsTooltipVisible(true);
      }, TOOLTIP_APPEARANCE_DEBOUNCE_TIME_MS);
    }
  };

  const handleMouseLeave = () => {
    if (isTooltipVisible) {
      tooltipTimeoutRef.current = window.setTimeout(() => {
        setIsTooltipVisible(false);
      }, TOOLTIP_APPEARANCE_DEBOUNCE_TIME_MS);
    } else {
      window.clearTimeout(tooltipTimeoutRef.current);
    }
  };

  const isExpired = entity.subscriptionInfo.type === "expired";

  // We skip the tooltip for small viewports since it's not readable anyways.
  // Subject to change once we come up with a dedicated mobile design.
  const unreadBell =
    !isExpired && entity.unread ? (
      isSmallViewport ? (
        <div className="flex h-8 w-8 items-center justify-center">
          <BellIcon />
        </div>
      ) : (
        <InteractiveContainer
          ref={refs.setReference}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-transparent transition-colors hover:bg-dark-100"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={(event) => event.stopPropagation()}
        >
          <BellIcon />
        </InteractiveContainer>
      )
    ) : null;

  return (
    <div className="relative font-secondary">
      {!isSmallViewport && (
        <EntityUpdatesTooltip
          ref={refs.setFloating}
          isVisible={isTooltipVisible}
          entityId={entity.id}
          style={floatingStyles}
        />
      )}
      <ListItemCard
        avatarSrc={entity.logoImg}
        subAvatarEl={entity.role === UserRole.admin ? <AdminIcon /> : undefined}
        title={entity.name}
        subTitle={
          isExpired ? (
            <span className="font-primary text-xs text-red-500">
              Subscription expired
            </span>
          ) : undefined
        }
        className={
          cx(
            "text-sm text-dark",
            isSelected &&
              variant === "popover" &&
              "bg-primary-50 hover:bg-primary-50"
          ) || ""
        }
        onClick={() => {
          if (!isSelected) {
            selectEntity(entity, showToastOnSwitch);
          }
          onEntitySwitch?.();
        }}
        rightContent={
          <div
            className={cx(
              "flex items-center",
              variant === "popover" ? "gap-2" : "gap-4"
            )}
          >
            {unreadBell}
            {variant === "popover" && isSelected && <SelectedIcon />}
            <Menu
              placement="bottom-end"
              sections={[
                [
                  {
                    isHidden:
                      entity.role !== "admin" ||
                      entity.subscriptionInfo.type !== "expired",
                    id: "open-update-subscription",
                    label: t({
                      id: "entities-page.update-subscription",
                      defaultMessage: "Update Subscription",
                    }),
                    icon: <StarsIcon />,
                    onSelect: (_, close) => {
                      selectEntity(
                        entity,
                        showToastOnSwitch,
                        generateUrl("workspaceNoSubscription", undefined, {
                          entityId: entity.id,
                        })
                      );
                      onEntitySwitch?.();
                      close();
                    },
                  },
                  {
                    isHidden:
                      entity.role !== "admin" ||
                      entity.subscriptionInfo.type === "expired",
                    id: "open-workspace-settings",
                    label: t({
                      id: "entities-page.open-workspace-settings",
                      defaultMessage: "Open Workspace Settings",
                    }),
                    icon: <StarsIcon />,
                    onSelect: (_, close) => {
                      if (entity.type === "org") {
                        selectEntity(
                          entity,
                          showToastOnSwitch,
                          generateUrl("workspaceFeatures", undefined, {
                            entityId: entity.id,
                          })
                        );
                      } else {
                        selectEntity(
                          entity,
                          showToastOnSwitch,
                          generateUrl("cxWorkspacePlans", undefined, {
                            entityId: entity.id,
                          })
                        );
                      }
                      onEntitySwitch?.();
                      close();
                    },
                  },
                  {
                    id: "leave-workspace",
                    label: t({
                      id: "entities-page.leave-workspace",
                      defaultMessage: "Leave workspace",
                    }),
                    icon: <LeaveIcon />,
                    onSelect: (_, close) => {
                      onLeaveEntity();
                      close();
                    },
                  },
                  {
                    isHidden: entity.role !== "admin" || !onDeleteEntity,
                    id: "delete-workspace",
                    label: t({
                      id: "entities-page.delete-workspace",
                      defaultMessage: "Delete Workspace",
                    }),
                    icon: (
                      <TrashIcon className={notRemovable ? "opacity-25" : ""} />
                    ),
                    endSlot: notRemovableReason ? (
                      <NotRemovableReasonHint reason={notRemovableReason} />
                    ) : undefined,
                    labelClassName: notRemovable ? "opacity-25" : undefined,
                    onSelect: (_, close) => {
                      if (notRemovable) {
                        return;
                      }
                      onDeleteEntity?.();
                      close();
                    },
                  },
                ],
              ]}
              renderTrigger={({ Trigger, triggerRef }) => (
                <Trigger
                  ref={triggerRef}
                  as={InteractiveContainer}
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-transparent transition-colors hover:bg-dark-100"
                  onClick={(event) => event.stopPropagation()}
                >
                  <MenuIcon />
                </Trigger>
              )}
            />
          </div>
        }
      />
    </div>
  );
};
